import { useEffect, useState, useCallback, useMemo } from 'react'
import { browserHistory } from 'react-router'
import qs from 'query-string'

/** Stable empty object for default options to avoid useCallback/useEffect churn */
const EMPTY_DEFAULTS: Record<string, unknown> = {}

/**
 * Configuration options for useSearchParams hook
 */
export interface UseSearchParamsOptions {
  /**
   * How to format arrays in the URL (query-string v4 from react-router: bracket | index | none)
   * - 'none': ?tags=a&tags=b (default)
   * - 'bracket': ?tags[]=a&tags[]=b
   * - 'index': ?tags[0]=a&tags[1]=b
   */
  arrayFormat?: 'bracket' | 'index' | 'none'

  /**
   * Whether to parse numbers and booleans automatically
   */
  parseNumbers?: boolean
  parseBooleans?: boolean

  /**
   * Default values when params are not in URL
   */
  defaults?: Record<string, unknown>
}

export interface SetParamsOptions {
  /** Replace current history entry instead of pushing new one */
  replace?: boolean
  /** Clear all existing params before setting new ones */
  reset?: boolean
}

/**
 * Generic hook for managing URL search params state.
 * Works with react-router v3's browserHistory.
 *
 * @example
 * // Basic usage with type safety
 * interface MyFilters { search?: string; page?: string }
 * const { params, setParams } = useSearchParams<MyFilters>()
 *
 * @example
 * // With defaults
 * const { params } = useSearchParams<Filters>({
 *   defaults: { page: '1', limit: '50' }
 * })
 *
 * @example
 * // Update params
 * setParams({ search: 'test', page: '1' })
 *
 * @example
 * // Clear a param by setting undefined
 * setParams({ search: undefined })
 */
export function useSearchParams<T extends object = Record<string, unknown>>(
  options: UseSearchParamsOptions = {}
): {
  /** Current URL params as typed object */
  params: T
  /** Set multiple params at once */
  setParams: (newParams: Partial<T>, options?: SetParamsOptions) => void
  /** Set a single param */
  setParam: <K extends keyof T>(key: K, value: T[K] | undefined) => void
  /** Clear all params */
  clearParams: () => void
  /** Clear a single param */
  clearParam: (key: keyof T) => void
  /** Check if a param exists and has a value */
  hasParam: (key: keyof T) => boolean
  /** Get a single param value */
  getParam: <K extends keyof T>(key: K) => T[K] | undefined
} {
  const {
    arrayFormat = 'none',
    parseNumbers: _parseNumbers = false,
    parseBooleans: _parseBooleans = false,
    defaults = EMPTY_DEFAULTS,
  } = options

  // query-string v4 (react-router dep) supports arrayFormat; parseNumbers/parseBooleans are ignored
  const parseOptions = useMemo(() => ({ arrayFormat }), [arrayFormat])

  const parseCurrentParams = useCallback((): T => {
    const location = browserHistory.getCurrentLocation()
    const parsed = qs.parse(location.search, parseOptions)
    return { ...defaults, ...parsed } as T
  }, [parseOptions, defaults])

  const [params, setParamsState] = useState<T>(parseCurrentParams)

  // Listen to URL changes (back/forward navigation)
  useEffect(() => {
    const unlisten = browserHistory.listen(() => {
      setParamsState(parseCurrentParams())
    })
    return unlisten
  }, [parseCurrentParams])

  const setParams = useCallback(
    (newParams: Partial<T>, opts: SetParamsOptions = {}) => {
      const location = browserHistory.getCurrentLocation()

      let baseParams: Record<string, unknown>
      if (opts.reset) {
        baseParams = {}
      } else {
        baseParams = qs.parse(location.search, parseOptions)
      }

      const merged = { ...baseParams, ...newParams }

      // Remove undefined/null/empty values; drop empty arrays so URL doesn't get key[]=
      Object.keys(merged).forEach((key) => {
        const value = merged[key]
        if (value === undefined || value === null || value === '') {
          delete merged[key]
        } else if (Array.isArray(value) && value.length === 0) {
          delete merged[key]
        }
      })

      // History's useQueries replace/push use location.query (not location.search) to build the URL.
      // Pass query so the wrapper's encodeQuery(location, location.query) stringifies our merged params.
      const newLocation = { ...location, query: merged }

      if (opts.replace) {
        browserHistory.replace(newLocation)
      } else {
        browserHistory.push(newLocation)
      }
    },
    [parseOptions]
  )

  const setParam = useCallback(
    <K extends keyof T>(key: K, value: T[K] | undefined) => {
      setParams({ [key]: value } as Partial<T>)
    },
    [setParams]
  )

  const clearParams = useCallback(() => {
    const location = browserHistory.getCurrentLocation()
    browserHistory.push({ ...location, query: {} })
  }, [])

  const clearParam = useCallback(
    (key: keyof T) => {
      setParams({ [key]: undefined } as Partial<T>)
    },
    [setParams]
  )

  const hasParam = useCallback(
    (key: keyof T): boolean => {
      const value = params[key]
      return value !== undefined && value !== null && value !== ''
    },
    [params]
  )

  const getParam = useCallback(
    <K extends keyof T>(key: K): T[K] | undefined => {
      return params[key]
    },
    [params]
  )

  return {
    params,
    setParams,
    setParam,
    clearParams,
    clearParam,
    hasParam,
    getParam,
  }
}

// Type helpers for common URL param patterns
export type StringParam = string | undefined
export type StringArrayParam = string | string[] | undefined
export type BooleanStringParam = 'true' | 'false' | undefined
export type NumberStringParam = string | undefined

/**
 * Serialize a query payload object into a URL query string, skipping null/undefined/empty values.
 * Arrays are serialized with bracket notation (e.g. `ids[]=1&ids[]=2`).
 *
 * @example
 * serializeQueryParams({ page: 1, ids: ['a', 'b'], empty: undefined })
 * // "page=1&ids[]=a&ids[]=b"
 */
export function serializeQueryParams<T extends object>(payload: T): string {
  return qs.stringify(payload, {
    skipNull: true,
    skipEmptyString: true,
    arrayFormat: 'bracket',
  })
}
